import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  HomeIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import { useCart } from '../../context/CartContext';
import { productService } from '../../services/product.service';
import { CustomBox } from '../../types/product';
import { showWarning, showSuccess } from '../../utils/notificationUtils';
import { Carousel } from 'antd';
import RichTextContent from '../../components/common/RichTextContent';
import {
  getProductImageList,
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';

const getProductWeight = (product: {
  weight_per_product?: number;
  name?: string;
}): number => {
  if (product.weight_per_product && product.weight_per_product > 0) {
    return product.weight_per_product;
  }

  const match = product.name?.match(/(\d+)\s*gr/i);
  return match ? parseInt(match[1], 10) : 100;
};

const CustomBoxDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedImage, setSelectedImage] = useState(0);

  const [error, setError] = useState<string | null>(null);
  const [boxData, setBoxData] = useState<CustomBox | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, number>
  >({});
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [totalPrice, setTotalPrice] = useState<number>(
    boxData?.initial_price || 0
  ); // Giá khởi điểm
  const [totalWeight, setTotalWeight] = useState<number>(0); // Tổng trọng lượng (gram)
  const [quantity, setQuantity] = useState<number>(1); // Số lượng hộp quà
  const [minWeightGram, setMinWeightGram] = useState<number>(400); // Trọng lượng tối thiểu (gram)
  const { addItem } = useCart();

  // Fetch box data when component mounts
  useEffect(() => {
    const fetchBoxData = async () => {
      setLoading(true);
      setError(null);

      try {
        const boxData = await productService.getCustomBoxDetail(slug || '');
        setMinWeightGram(boxData.min_weight || 400);
        setBoxData(boxData);
        setTotalPrice(boxData.initial_price || 0);

        const initialQuantities: Record<string, number> = {};
        boxData.products?.forEach((product) => {
          initialQuantities[product.id] = 0;
        });
        setSelectedProducts(initialQuantities);
      } catch (error) {
        console.error('Error fetching data:', error);
        const message =
          error instanceof Error
            ? error.message
            : 'Không thể tải dữ liệu sản phẩm';
        setError(message);
        setBoxData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBoxData();
  }, [slug]);

  // Filtering products based on search term
  const filteredProducts = boxData?.products
    ?.filter((p) => p.status === 'active')
    .filter((p) => p.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Toggle product selection - don't allow selecting disabled products
  const toggleProductSelection = (productId: string) => {
    const product = boxData?.products?.find((p) => p.id === productId);

    // Don't allow selecting disabled or out of stock products
    if (product && product.status !== 'active') {
      return;
    }

    setSelectedItems((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(productId)) {
        newSet.delete(productId);
        // Reset quantity
        setSelectedProducts((prev) => ({
          ...prev,
          [productId]: 0,
        }));
      } else {
        newSet.add(productId);
        // Set initial quantity to 1
        setSelectedProducts((prev) => ({
          ...prev,
          [productId]: 1,
        }));
      }

      return newSet;
    });
  };

  // Handle changing product quantity
  const handleQuantityChange = (productId: string, change: number) => {
    setSelectedProducts((prev) => {
      const currentQty = prev[productId] || 0;
      const product = boxData?.products?.find((p) => p.id === productId);

      if (!product) return prev;

      // Ensure quantity is within limits
      const newQty = Math.max(
        product.min_quantity || 0,
        Math.min(currentQty + change, product.max_quantity || 0)
      );

      return {
        ...prev,
        [productId]: newQty,
      };
    });
  };

  // Calculate total price and weight whenever selections change
  useEffect(() => {
    if (!boxData) return;

    let total = boxData?.initial_price || 0; // Base price
    let weight = 0; // Reset weight

    Object.entries(selectedProducts).forEach(([id, qty]) => {
      const product = boxData?.products?.find((p) => p.id === id);
      if (product && product.original_price && qty > 0) {
        total += product.original_price * qty;
        weight += getProductWeight(product) * qty;
      }
    });

    // Total price for all boxes
    setTotalPrice(total);
    setTotalWeight(weight);
  }, [selectedProducts, boxData]);

  // Handle adding custom box to cart
  const handleAddToCart = () => {
    if (!boxData) return;

    // Check if any products have been selected
    const hasSelectedProducts = Object.values(selectedProducts).some(
      (qty) => qty > 0
    );

    if (!hasSelectedProducts) {
      showWarning(
        'Chưa chọn trái cây',
        'Vui lòng chọn ít nhất một loại trái cây cho hộp quà của bạn.'
      );
      return;
    }

    // Check if total weight meets the minimum requirement
    if (totalWeight < boxData?.min_weight!) {
      showWarning(
        'Trọng lượng chưa đủ',
        `Tổng trọng lượng trái cây phải ít nhất ${boxData?.min_weight}g. Hiện tại: ${totalWeight}g.`
      );
      return;
    }

    // Get the selected items with their full details
    const selectedItemDetails = Object.entries(selectedProducts)
      .filter(([_, qty]) => qty > 0)
      .map(([id, quantity]) => {
        const product = boxData?.products?.find((p) => p.id === id);
        return {
          id,
          name: product?.name || '',
          price: product?.original_price || 0,
          unit: product?.unit || '',
          quantity,
          weight: getProductWeight(product!) * quantity,
        };
      });

    // Calculate fruits total price
    const fruitsTotal = Object.entries(selectedProducts)
      .filter(([_, qty]) => qty > 0)
      .reduce((total, [id, qty]) => {
        const product = boxData?.products?.find((p) => p.id === id);
        return total + (product?.original_price || 0) * qty;
      }, 0);

    // Create the custom box object
    const customBox = {
      id: boxData.id,
      name: boxData.name,
      price: totalPrice,
      initial_price: boxData.initial_price,
      fruits_price: fruitsTotal,
      image: getProductImageUrl(boxData?.images?.[0]),
      selectedItems: selectedItemDetails,
      type: 'fruit_box',
      totalWeight: totalWeight,
    };

    // Show confirmation with selected items before adding to cart
    const selectedItemsText = selectedItemDetails
      .map(
        (item) =>
          `- ${item.name} (${item.quantity} ${item.unit}, ${item.weight}g)`
      )
      .join('\n');

    const confirmMessage = `Bạn đã chọn:\n${selectedItemsText}\n\nTổng trọng lượng: ${totalWeight}g\nGiá hộp: ${boxData.initial_price?.toLocaleString()}₫\nGiá trái cây: ${fruitsTotal.toLocaleString()}₫\nTổng tiền: ${totalPrice.toLocaleString()}₫ × ${quantity} = ${(
      totalPrice * quantity
    ).toLocaleString()}₫\n\nThêm vào giỏ hàng?`;

    // if (window.confirm(confirmMessage)) {
    addItem(customBox, quantity);
    showSuccess(
      'Thêm vào giỏ hàng',
      'Sản phẩm đã được thêm vào giỏ hàng thành công'
    );
    // }
  };

  // Loading state
  if (loading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <div className='flex items-center justify-center h-64'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !boxData) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <div className='bg-red-50 text-red-700 p-4 rounded-md'>
          <p>{error || 'Không tìm thấy sản phẩm'}</p>
        </div>
      </div>
    );
  }

  const boxImages = getProductImageList(boxData.images);

  return (
    <div className='container mx-auto px-4 py-8 font-["Quicksand"]'>
      {/* Breadcrumb Navigation */}
      <nav className='flex items-center mb-6 text-base'>
        <RouterLink
          to='/'
          className='flex items-center text-primary hover:text-primary-dark font-medium'
        >
          <HomeIcon className='h-5 w-5 mr-1.5' />
          <span>Trang chủ</span>
        </RouterLink>
        {/* <ChevronRightIcon className='h-5 w-5 mx-3 text-gray-400' />
        <RouterLink
          to='/categories/qua-tang-trai-cay'
          className='text-primary hover:text-primary-dark font-medium'
        >
          Quà tặng trái cây
        </RouterLink> */}
        <ChevronRightIcon className='h-5 w-5 mx-3 text-gray-400' />
        <span className='text-gray-800 font-medium truncate'>
          {boxData.name}
        </span>
      </nav>

      {/* Mobile-first layout */}
      <div className='grid grid-cols-1 lg:grid-cols-5 gap-6'>
        {/* Left column - full width on mobile, 2/5 on desktop */}
        <div className='lg:col-span-2'>
          <div className='bg-white rounded-lg shadow-sm overflow-hidden p-4 sm:p-6 border border-gray-100'>
            {/* Product image */}
            <div className='mb-4 aspect-square rounded-lg overflow-hidden bg-gray-50 p-6 max-w-[450px] mx-auto'>
              <img
                src={boxImages[selectedImage]}
                alt={boxData.name}
                className='w-full h-full object-contain'
                onError={handleProductImageError}
              />
            </div>
            <div className='grid grid-cols-4 gap-2 mb-4'>
              {boxImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square rounded-md overflow-hidden border-2 ${
                    selectedImage === index
                      ? 'border-primary'
                      : 'border-transparent'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${boxData?.name} - Hình ${index + 1}`}
                    className='w-full h-full object-cover'
                    onError={handleProductImageError}
                  />
                </button>
              ))}
            </div>

            {/* Product info - adapt text size for mobile */}
            {/* <h1 className='text-2xl sm:text-3xl font-bold text-primary mb-4 font-["Quicksand"]'>
              {boxData.name}
            </h1> */}

            <p className='text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 leading-relaxed'>
              <RichTextContent content={boxData?.description || ''} />
            </p>

            {/* Base price */}
            {/* <div className='mb-4 pb-4 border-b border-gray-200'>
              <div className='flex items-center justify-between'>
                <span className='text-sm sm:text-base text-gray-700 font-medium'>
                  Giá khởi điểm:
                </span>
                <span className='text-sm sm:text-base text-primary font-medium'>
                  {boxData?.initial_price?.toLocaleString('vi-VN')}₫
                </span>
              </div>
            </div> */}

            {/* Additional info - hide on small mobile */}
            <div
              className='hidden sm:block p-4 rounded-lg mb-4'
              style={{
                backgroundColor: 'rgba(229, 62, 62, 0.05)',
                borderColor: 'rgba(229, 62, 62, 0.2)',
                border: '1px solid',
              }}
            >
              <p className='text-sm text-gray-600'>
                Chọn các loại trái cây bạn muốn cho hộp quà của mình. Giá sẽ
                được tính dựa trên số lượng và loại trái cây bạn chọn.
              </p>
            </div>
          </div>
        </div>

        {/* Right column - full width on mobile, 3/5 on desktop */}
        <div className='lg:col-span-3'>
          {/* Product selection area */}
          <div className='bg-white rounded-lg shadow-sm overflow-hidden mb-6 p-4 sm:p-6 border border-gray-100'>
            {/* <h2 className='text-xl font-bold text-gray-800 mb-4 font-["Quicksand"]'>
              Chọn trái cây cho hộp quà của bạn
            </h2> */}
            {/* Product info - adapt text size for mobile */}
            <h1 className='text-2xl sm:text-3xl font-bold text-primary mb-4 font-["Quicksand"]'>
              {boxData.name}
            </h1>

            {/* Search bar */}
            <div className='relative mb-4'>
              <input
                type='text'
                placeholder='Tìm kiếm trái cây...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
              />
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                >
                  <svg
                    className='h-5 w-5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Results count */}
            <p className='text-sm text-gray-500 mb-4'>
              {filteredProducts?.length === 0
                ? 'Không tìm thấy sản phẩm nào'
                : `Hiển thị ${filteredProducts?.length} sản phẩm`}
            </p>

            {/* Scrollable product list */}
            <div className='max-h-[400px] overflow-y-auto pr-1 mb-6 sm:mb-8'>
              <div className='space-y-2 sm:space-y-3'>
                {filteredProducts?.map((product) => {
                  const isDisabled =
                    product.status === 'disabled' ||
                    product.status === 'out_of_stock';
                  const productImages = getProductImageList(product.images);
                  const productWeight = getProductWeight(product);

                  return (
                    <div
                      key={product.id}
                      onClick={() =>
                        !isDisabled && toggleProductSelection(product.id)
                      }
                      className={`bg-white border rounded-lg overflow-hidden shadow-sm transition-all duration-200 
                        ${
                          isDisabled
                            ? 'opacity-60 cursor-not-allowed'
                            : 'cursor-pointer'
                        }
                        ${
                          selectedItems.has(product.id)
                            ? 'border-primary ring-1 ring-primary ring-opacity-50'
                            : 'border-gray-200 hover:border-primary'
                        }
                      `}
                    >
                      <div className='flex items-center p-2 sm:p-3 relative'>
                        {/* Checkbox - disabled if product is not available */}
                        <div className='mr-2'>
                          <input
                            type='checkbox'
                            checked={selectedItems.has(product.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (!isDisabled)
                                toggleProductSelection(product.id);
                            }}
                            disabled={isDisabled}
                            className={`w-4 h-4 border-gray-300 rounded focus:ring-primary
                              ${
                                isDisabled
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'text-primary'
                              }
                            `}
                          />
                        </div>

                        {/* Product image - smaller on mobile */}
                        <div className='w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-md overflow-hidden mr-2 sm:mr-3 relative'>
                          <Carousel
                            className='w-full h-full object-cover'
                            autoplay={
                              productImages.length > 1
                                ? { dotDuration: true }
                                : false
                            }
                            autoplaySpeed={5000}
                          >
                            {productImages.map((image, imageIndex) => (
                              <div key={`${product.id}-${imageIndex}`}>
                                <img
                                  src={image}
                                  alt={product.name}
                                  className='w-full h-full object-cover'
                                  onError={handleProductImageError}
                                />
                              </div>
                            ))}
                          </Carousel>
                          {/* Status overlay for out of stock */}
                          {product.status === 'out_of_stock' && (
                            <div className='absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center'>
                              <span className='text-white text-xs font-medium'>
                                Hết hàng
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Product name and price */}
                        <div className='flex-1 min-w-0'>
                          <h3 className='font-medium text-gray-900 text-xs sm:text-sm truncate'>
                            {product.name}
                            {product.status !== 'active' && (
                              <span className='ml-1 text-xs text-gray-500'>
                                (Không khả dụng)
                              </span>
                            )}
                          </h3>
                          <p className='text-gray-600 text-xs'>
                            {product.original_price?.toLocaleString('vi-VN')} ₫
                            / {productWeight}g
                          </p>
                        </div>

                        {/* Quantity controls */}
                        <div className='flex items-center ml-auto'>
                          {selectedItems.has(product.id) ? (
                            <div className='flex items-center border border-gray-300 rounded-md'>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuantityChange(product.id, -1);
                                }}
                                className='p-1 text-gray-600 hover:text-primary disabled:opacity-50'
                                disabled={
                                  selectedProducts[product.id] <=
                                    product.min_quantity! || isDisabled
                                }
                              >
                                <MinusIcon className='h-3 w-3' />
                              </button>
                              <span className='px-2 py-0.5 border-x border-gray-300 min-w-[24px] text-center text-sm'>
                                {selectedProducts[product.id] || 0}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuantityChange(product.id, 1);
                                }}
                                className='p-1 text-gray-600 hover:text-primary disabled:opacity-50'
                                disabled={
                                  selectedProducts[product.id] >=
                                    product.max_quantity! || isDisabled
                                }
                              >
                                <PlusIcon className='h-3 w-3' />
                              </button>
                            </div>
                          ) : !isDisabled ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleProductSelection(product.id);
                              }}
                              className='text-xs text-primary hover:text-primary-dark'
                            >
                              Chọn
                            </button>
                          ) : null}
                        </div>

                        {/* Product price total - hide on smallest screens */}
                        {selectedItems.has(product.id) &&
                          selectedProducts[product.id] > 0 && (
                            <div className='hidden sm:block ml-3 text-right'>
                              <span className='font-medium text-primary text-xs sm:text-sm'>
                                {(
                                  (selectedProducts[product.id] || 0) *
                                  (product.original_price || 0)
                                ).toLocaleString('vi-VN')}
                                ₫
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Checkout section */}
          <div className='sticky bottom-0 bg-white rounded-lg shadow-sm overflow-hidden p-4 sm:p-6 border border-gray-100'>
            <div className='flex flex-col space-y-4'>
              {/* Weight indicator */}
              <div className='flex justify-between items-center'>
                <span className='text-base text-gray-800 font-medium font-["Quicksand"] flex items-center'>
                  <ScaleIcon className='h-4 w-4 mr-1.5' />
                  Tổng trọng lượng:
                </span>
                <div
                  className={`font-medium ${
                    totalWeight < minWeightGram
                      ? 'text-red-500'
                      : 'text-green-600'
                  }`}
                >
                  {totalWeight}g{' '}
                  {totalWeight < minWeightGram &&
                    `(Tối thiểu ${minWeightGram}g)`}
                </div>
              </div>

              {/* Price breakdown */}
              <div className='space-y-2 border-t border-gray-100 pt-3'>
                <div className='flex justify-between items-center'>
                  <span className='text-sm text-gray-600'>Giá hộp:</span>
                  <span className='text-sm text-gray-800 font-medium'>
                    {boxData.initial_price?.toLocaleString()}₫
                  </span>
                </div>

                <div className='flex justify-between items-center'>
                  <span className='text-sm text-gray-600'>Giá trái cây:</span>
                  <span className='text-sm text-gray-800 font-medium'>
                    {Object.entries(selectedProducts)
                      .filter(([_, qty]) => qty > 0)
                      .reduce((total, [id, qty]) => {
                        const product = boxData?.products?.find(
                          (p) => p.id === id
                        );
                        return total + (product?.original_price || 0) * qty;
                      }, 0)
                      .toLocaleString()}
                    ₫
                  </span>
                </div>
              </div>

              <div className='flex justify-between items-center'>
                <span className='text-base text-gray-800 font-medium font-["Quicksand"]'>
                  Số lượng:
                </span>
                <div className='flex items-center border border-gray-300 rounded-md'>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className='p-1.5 text-gray-600 hover:text-primary disabled:opacity-50'
                    disabled={quantity <= 1}
                  >
                    <MinusIcon className='h-4 w-4' />
                  </button>
                  <span className='px-3 py-1 border-x border-gray-300 min-w-[32px] text-center'>
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className='p-1.5 text-gray-600 hover:text-primary'
                  >
                    <PlusIcon className='h-4 w-4' />
                  </button>
                </div>
              </div>

              <div className='flex justify-between items-center'>
                <span className='text-base text-gray-800 font-medium font-["Quicksand"]'>
                  Tổng tiền:
                </span>
                <span className='text-xl text-primary font-bold'>
                  {(totalPrice * quantity).toLocaleString()}₫
                </span>
              </div>

              <button
                onClick={handleAddToCart}
                className='flex items-center justify-center w-full py-3 bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary/20 text-white font-medium rounded-lg text-base transition-colors'
                disabled={
                  !Object.values(selectedProducts).some((qty) => qty > 0) ||
                  totalWeight < minWeightGram
                }
              >
                <ShoppingCartIcon className='h-5 w-5 mr-2' />
                Thêm vào giỏ hàng
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomBoxDetail;
